import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Clock, Video, ExternalLink, User, MapPin, Loader2, CalendarX } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

export default function UpcomingBookings({ childId, onJoinMeet }) {
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);

  const loadBookings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/bookings/child/${childId}/upcoming`);
      setBookings(response.data.bookings || []);
    } catch (error) {
      // No upcoming bookings - expected state
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isToday = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isTomorrow = (dateStr) => {
    const date = new Date(dateStr);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date.toDateString() === tomorrow.toDateString();
  };

  const getDateLabel = (dateStr) => {
    if (isToday(dateStr)) return 'Today';
    if (isTomorrow(dateStr)) return 'Tomorrow';
    return formatDate(dateStr);
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Calendar className="h-5 w-5 text-[#02b5ff]" />
          <h3 className="text-lg font-bold text-white">Upcoming Bookings</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 text-[#02b5ff] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-6" data-testid="upcoming-bookings">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Calendar className="h-5 w-5 text-[#02b5ff]" />
          <h3 className="text-lg font-bold text-white">Upcoming Bookings</h3>
        </div>
        <a
          href="https://eliteplayerperformance.setmore.com/book"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-[#02b5ff] hover:text-white transition-colors flex items-center gap-1"
        >
          Book New
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {bookings.length === 0 ? (
        <div className="text-center py-8">
          <CalendarX className="h-10 w-10 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm mb-4">No upcoming bookings</p>
          <a
            href="https://eliteplayerperformance.setmore.com/book?step=staff&products=athlete-growth-pro&type=class"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#02b5ff] hover:bg-[#0099dd] text-white text-sm font-medium rounded-lg transition-colors"
          >
            Book 1-2-1 Testing
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map((booking, idx) => (
            <div
              key={booking.id || idx}
              className={`bg-zinc-800/50 border rounded-lg p-4 ${
                isToday(booking.scheduled_date) 
                  ? 'border-[#02b5ff]/50 bg-[#02b5ff]/5' 
                  : 'border-zinc-700'
              }`}
              data-testid={`booking-${idx}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  {/* Service Name */}
                  <h4 className="text-white font-semibold mb-2">
                    {booking.service_name || 'Booking'}
                  </h4>
                  
                  {/* Date & Time */}
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Calendar className="h-4 w-4" />
                      <span className={isToday(booking.scheduled_date) ? 'text-[#02b5ff] font-medium' : ''}>
                        {getDateLabel(booking.scheduled_date)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Clock className="h-4 w-4" />
                      <span>{formatTime(booking.scheduled_date)}</span>
                      {booking.duration_minutes && (
                        <span className="text-zinc-600">({booking.duration_minutes} min)</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Staff */}
                  {booking.staff_name && (
                    <div className="flex items-center gap-2 text-sm text-zinc-500 mt-2">
                      <User className="h-4 w-4" />
                      <span>{booking.staff_name}</span>
                    </div>
                  )}
                  
                  {/* Notes */}
                  {booking.notes && (
                    <p className="text-xs text-zinc-500 mt-2 italic">
                      {booking.notes}
                    </p>
                  )}
                </div>
                
                {/* Video Call Button */}
                {(booking.google_meet_link || booking.is_video_call) && (
                  <div className="flex-shrink-0">
                    {booking.google_meet_link ? (
                      <Button
                        onClick={() => onJoinMeet ? onJoinMeet(booking.google_meet_link) : window.open(booking.google_meet_link, '_blank')}
                        className="bg-green-600 hover:bg-green-500 text-white"
                        size="sm"
                        data-testid={`join-meet-${idx}`}
                      >
                        <Video className="h-4 w-4 mr-2" />
                        Join Video Call
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2 text-green-400 text-sm">
                        <Video className="h-4 w-4" />
                        <span>Video Call</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Today indicator */}
              {isToday(booking.scheduled_date) && (
                <div className="mt-3 pt-3 border-t border-zinc-700">
                  <span className="text-xs bg-[#02b5ff]/20 text-[#02b5ff] px-2 py-1 rounded">
                    Happening Today
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
